import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BackNavLink } from '../../components/NavButtons';
import { useGradingSystem } from '../../contexts/GradingSystemContext';
import { getReportCardById } from '../../services/gradeService';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { resolveGradeLabel } from '../../utils/gradingUtils';

function formatReportCardDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function getTeacherName(teacher) {
  return [teacher?.firstName, teacher?.lastName].filter(Boolean).join(' ') || 'معلم';
}

function hasDescription(value) {
  if (value == null) {
    return false;
  }

  if (typeof value !== 'string') {
    return String(value).trim().length > 0;
  }

  return value.trim().length > 0;
}

function DescriptionBlock({ label = 'توضیحات', children, className = '' }) {
  if (!hasDescription(children)) {
    return null;
  }

  return (
    <div className={`rounded-[8px] bg-[#F8FAFC] px-3 py-2 ${className}`}>
      <p className="text-[10px] text-slate-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-[11px] leading-6 text-slate-700">{children}</p>
    </div>
  );
}

function GradeDetailsPage() {
  const { reportCardId } = useParams();
  const { gradingSystem, disciplineSystem } = useGradingSystem();
  const [reportCard, setReportCard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const sortedItems = useMemo(
    () => [...(reportCard?.items || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [reportCard]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadReportCard() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const reportCardData = await getReportCardById(reportCardId);

        if (isMounted) {
          setReportCard(reportCardData);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت جزئیات کارنامه وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadReportCard();

    return () => {
      isMounted = false;
    };
  }, [reportCardId]);

  return (
    <div className="space-y-5">
      <BackNavLink to="/grades">بازگشت به کارنامه‌ها</BackNavLink>

      <div className="flex items-center gap-2">
        <p
          style={{
            background: 'linear-gradient(90deg, #A7D2FB 0%, #49A8F4 100%)',
            boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)',
          }}
          className="rounded-[10px] px-6 py-1 text-[15px] text-white"
        >
          جزئیات کارنامه
        </p>

        <img className="h-[50px]" src="/images/grade.webp" alt="" />
      </div>

      {isLoading ? (
        <div className="rounded-[14px] bg-white p-6 text-center text-[12px] text-slate-500 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          در حال دریافت جزئیات کارنامه...
        </div>
      ) : errorMessage ? (
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[13px] font-bold text-[#E02727]">{errorMessage}</p>
          <Link
            to="/grades"
            className="mt-3 inline-flex rounded-[6px] bg-[#49A8F4] px-5 py-1 text-[12px] font-bold text-white"
          >
            بازگشت به لیست
          </Link>
        </div>
      ) : !reportCard ? (
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[13px] font-bold text-slate-800">کارنامه پیدا نشد</p>
          <Link
            to="/grades"
            className="mt-3 inline-flex rounded-[6px] bg-[#49A8F4] px-5 py-1 text-[12px] font-bold text-white"
          >
            بازگشت به لیست
          </Link>
        </div>
      ) : (
        <>
          <article className="overflow-hidden rounded-[12px] bg-white shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
            <div
              className="px-4 py-3 text-white"
              style={{ background: 'linear-gradient(90deg, #A7D2FB 0%, #49A8F4 100%)' }}
            >
              <p className="text-[10px] font-light text-white/90">
                {reportCard.class?.grade?.title || 'پایه تحصیلی'}
              </p>
              <p className="mt-0.5 text-[15px] font-bold">{reportCard.title || 'کارنامه'}</p>
              <p className="mt-1 text-[11px] font-light text-white/90">
                {reportCard.class?.title || 'کلاس'} · {formatReportCardDate(reportCard.date)}
              </p>
            </div>

            <div className="space-y-4 p-4">
              <div className="flex items-center gap-3">
                <img
                  src={getAvatarUrl(reportCard.teacher?.avatar)}
                  alt=""
                  className="h-[48px] w-[48px] shrink-0 rounded-full object-cover"
                />
                <div>
                  <p className="text-[10px] text-slate-500">معلم</p>
                  <p className="mt-0.5 text-[14px] font-bold text-slate-800">
                    {getTeacherName(reportCard.teacher)}
                  </p>
                </div>
              </div>

              {hasDescription(reportCard.description) && (
                <DescriptionBlock label="توضیحات کارنامه">
                  {reportCard.description}
                </DescriptionBlock>
              )}

              {(reportCard.discipline?.score || hasDescription(reportCard.discipline?.description)) && (
                <div
                  className="space-y-2 rounded-[10px] px-3 py-2.5"
                  style={{ background: 'rgba(73, 168, 244, 0.12)' }}
                >
                  <p className="text-[10px] text-slate-500">شایستگی های عمومی</p>
                  {reportCard.discipline?.score && (
                    <p className="text-[14px] font-bold text-[#49A8F4]">
                      {resolveGradeLabel(
                        reportCard.discipline.score,
                        reportCard.discipline.scoreLabel,
                        disciplineSystem
                      )}
                    </p>
                  )}
                  <DescriptionBlock
                    label="توضیحات شایستگی های عمومی"
                    className="bg-white/70"
                  >
                    {reportCard.discipline?.description}
                  </DescriptionBlock>
                </div>
              )}
            </div>
          </article>

          <section className="space-y-2">
            <p className="text-[12px] font-bold text-slate-800">نمرات دروس</p>

            {sortedItems.length === 0 ? (
              <div className="rounded-[10px] bg-white px-4 py-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
                <p className="text-[12px] text-slate-500">درسی برای این کارنامه ثبت نشده است.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {sortedItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="rounded-[10px] bg-white px-4 py-3 shadow-[0_4px_14px_rgba(15,23,42,0.06)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] text-slate-500">درس {index + 1}</p>
                        <p className="mt-0.5 text-[14px] font-bold text-slate-800">
                          {item.title || 'بدون عنوان'}
                        </p>
                      </div>

                      <span
                        className="shrink-0 rounded-[8px] px-3 py-1.5 text-[13px] font-bold text-white"
                        style={{ background: '#49A8F4' }}
                      >
                        {item.score
                          ? resolveGradeLabel(item.score, item.scoreLabel, gradingSystem)
                          : '-'}
                      </span>
                    </div>

                    <DescriptionBlock label="توضیحات درس" className="mt-2">
                      {item.description}
                    </DescriptionBlock>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default GradeDetailsPage;
